<script setup lang="ts">
import type { NuxtError } from '#app';
import type { AppErrorProperties } from '@optionway/models';

type AppError = AppErrorProperties & { error: NuxtError };

const properties = defineProps<AppError>();

const { $appConfig } = useNuxtApp();
const config = $appConfig.app?.appError;

const errorConfig = computed<AppError>(() => {
  return {
    error: properties.error,
    hideComponents: config?.hideComponents,
  };
});
const head = useLocaleHead();
const { t } = useI18n();
// computed: the title must follow a locale change (same fix as app.vue) — and the
// partner title is a literal, not an i18n key: only the fallback goes through t() (same as app.vue).
const metaTitle = computed(() => $appConfig.app?.title ?? t('app.title'));
const metaDescription = computed(() => t('app.seo.description'));

// SEO — no manual ogTitle (derived from the title, og:site_name carries the brand), and the
// description follows the localized key instead of the hardcoded English line app.vue dropped.
useSeoMeta({
  title: metaTitle,
  description: metaDescription,
  ogDescription: metaDescription,
});

// Multi-Cache configuration
useRouteCache((helper) => helper.setCacheable().setMaxAge('never').setStaleIfError('never'));
useCDNHeaders((helper) =>
  helper
    .public()
    .setNumeric('maxAge', 0)
    .set('staleIfError', 0)
    .set('staleWhileRevalidate', 0)
    .set('mustRevalidate', true),
);
</script>

<template>
  <Html
    :lang="head.htmlAttrs?.lang"
    :dir="head.htmlAttrs?.dir"
  >
    <Head>
      <template
        v-for="link in head.link"
        :key="link.id"
      >
        <Link
          :id="link.id"
          :rel="link.rel"
          :href="link.href"
          :hreflang="link.hreflang"
          :nonce="link.nonce"
        />
      </template>
      <template
        v-for="meta in head.meta"
        :key="meta.id"
      >
        <Meta
          :id="meta.id"
          :property="meta.property"
          :content="meta.content"
        />
      </template>
    </Head>
    <NuxtLayout>
      <main>
        <CommonErrorCodeDisplay :error="errorConfig.error" />
      </main>
    </NuxtLayout>
  </Html>
</template>
